/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Footer from "../components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaAward,
    FaExternalLinkAlt,
    FaChevronLeft,
    FaChevronRight,
    FaUser,
    FaStar,
    FaImage,
    FaTruck,
    FaFilm,
    FaHotel,
    FaCreditCard,
    FaIndustry,
    FaBrain,
    FaGraduationCap,
    FaHandsHelping,
    FaPalette,
    FaUtensils,
    FaLaptopCode,
    FaVideo,
    FaUsers,
    FaShoppingCart,
    FaBuilding,
    FaHeart,
    FaEdit,
    FaTrashAlt,
    FaPlus,
    FaSpinner,
    FaEye,
    FaEyeSlash,
    FaSearch,
    FaFilter,
    FaCalendarAlt,
    FaTrophy,
    FaPlay,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaExclamationTriangle,
    FaUserPlus,
    FaUserClock,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AwardsGallery from './AwardsGallery'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_AWARD_BANNER = `${API_BASE_URL}/UIaward-banner`;
const API_TOGETHER_WITH_MASA = `${API_BASE_URL}/UItogether-with-masa`;
const API_PARTICIPATES = `${API_BASE_URL}/UIparticipates`;
const API_MEDIA = `${API_BASE_URL}/UImedia`;

// Icon mapping for participation categories
const iconMap = {
    "Logistics": FaTruck,
    "Entertainment": FaFilm,
    "Hospitality": FaHotel,
    "Fintech": FaCreditCard,
    "Manufacturing": FaIndustry,
    "Mental Wellness": FaBrain,
    "Education Technology": FaGraduationCap,
    "NGO sector": FaHandsHelping,
    "Visual Art": FaPalette,
    "Food Tech": FaUtensils,
    "Freelance": FaLaptopCode,
    "Blog & Vlog": FaVideo,
    "SC/ST/OBC Welfare": FaUsers,
    "e-Commerce": FaShoppingCart,
    "Private Sector": FaBuilding,
    "Health & Hygiene": FaHeart,
    "default": FaAward
};

// Enhanced Carousel Component with Dynamic API Data
function AwardsCarousel() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [direction, setDirection] = useState(0);
    const [carouselSlides, setCarouselSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imageUrls, setImageUrls] = useState({});

    // ✅ ADDED: Handle banner click to open URL
    const handleBannerClick = (banner) => {
        if (banner && banner.url) {
            let url = banner.url;
            // Add https:// if not present
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
            }
            // Open in new tab with security attributes
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // Fetch award banners from API
    const fetchAwardBanners = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_AWARD_BANNER);
            const banners = response.data.docs || response.data.results || response.data;

            // Filter only active banners
            const activeBanners = Array.isArray(banners)
                ? banners.filter(banner => banner.isActive !== false)
                : [];

            setCarouselSlides(activeBanners);

            // Fetch images for each banner
            if (activeBanners.length > 0) {
                await fetchImages(activeBanners);
            }
        } catch (error) {
            console.error("❌ Error fetching award banners:", error);
            setCarouselSlides([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch images for each banner
    const fetchImages = async (banners) => {
        const urls = {};

        for (const banner of banners) {
            if (banner.image?.mediaId) {
                try {
                    const imageUrl = await getImageUrl(banner.image.mediaId);
                    urls[banner._id || banner.id] = imageUrl;
                } catch (error) {
                    console.error(`❌ Error fetching image for banner ${banner._id}:`, error);
                    urls[banner._id || banner.id] = "https://via.placeholder.com/800x400/1f2937/6b7280?text=No+Image";
                }
            } else {
                urls[banner._id || banner.id] = "https://via.placeholder.com/800x400/1f2937/6b7280?text=No+Image";
            }
        }

        setImageUrls(urls);
    };

    // Get image URL from media ID
    const getImageUrl = async (mediaId) => {
        try {
            const response = await axios.get(`${API_MEDIA}/${mediaId}/download`, {
                responseType: 'blob'
            });
            return URL.createObjectURL(response.data);
        } catch (error) {
            console.error(`❌ Error downloading media ${mediaId}:`, error);
            throw error;
        }
    };

    useEffect(() => {
        fetchAwardBanners();
    }, []);

    const nextSlide = useCallback(() => {
        if (carouselSlides.length === 0) return;
        setDirection(1);
        setCurrentSlide((prev) => (prev === carouselSlides.length - 1 ? 0 : prev + 1));
    }, [carouselSlides.length]);

    const prevSlide = () => {
        if (carouselSlides.length === 0) return;
        setDirection(-1);
        setCurrentSlide((prev) => (prev === 0 ? carouselSlides.length - 1 : prev - 1));
    };

    const goToSlide = (index) => {
        if (carouselSlides.length === 0) return;
        setDirection(index > currentSlide ? 1 : -1);
        setCurrentSlide(index);
    };

    // Auto-play functionality
    useEffect(() => {
        if (!isAutoPlaying || carouselSlides.length === 0) return;

        const interval = setInterval(() => {
            nextSlide();
        }, 5000);

        return () => clearInterval(interval);
    }, [currentSlide, isAutoPlaying, nextSlide, carouselSlides.length]);

    // Animation variants for slide transitions
    const slideVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0,
            scale: 0.8
        }),
        center: {
            x: 0,
            opacity: 1,
            scale: 1
        },
        exit: (direction) => ({
            x: direction > 0 ? -1000 : 1000,
            opacity: 0,
            scale: 0.8
        })
    };

    const contentVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                delay: 0.4
            }
        }
    };

    if (loading) {
        return (
            <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl bg-gray-800 flex items-center justify-center">
                <div className="text-center">
                    <FaAward className="text-yellow-400 text-4xl mx-auto mb-4 animate-pulse" />
                    <p className="text-white text-lg">Loading award banners...</p>
                </div>
            </div>
        );
    }

    if (carouselSlides.length === 0) {
        return (
            <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <FaAward className="text-yellow-400 text-4xl mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">No Award Banners Available</h3>
                    <p className="text-gray-300">Check back later for exciting award announcements.</p>
                </div>
            </div>
        );
    }

    const currentSlideData = carouselSlides[currentSlide];
    const currentImageUrl = imageUrls[currentSlideData._id || currentSlideData.id];

    return (
        <div
            className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
        >
            {/* Slides */}
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={currentSlideData._id || currentSlideData.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.4 },
                        scale: { duration: 0.5 }
                    }}
                    className="absolute inset-0"
                    onClick={() => handleBannerClick(currentSlideData)} // ✅ Main div par bhi click handler
                    style={{ cursor: currentSlideData?.url ? 'pointer' : 'default' }}
                >
                    {/* Background Image with Click Handler */}
                    <motion.img
                        src={currentImageUrl}
                        alt={currentSlideData.title}
                        className="w-full h-full object-continer"
                        // initial={{ scale: 1.1 }}
                        // animate={{ scale: 1 }}
                        // transition={{ duration: 8, ease: "easeOut" }}
                        onClick={() => handleBannerClick(currentSlideData)} // ✅ Image par bhi
                    />

                    {/* ✅ FIXED: Gradient Overlay - pointer-events-none daalo */}
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60 pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                    />

                    {/* ✅ FIXED: Pattern Overlay - pointer-events-none daalo */}
                    <motion.div
                        className="absolute inset-0 bg-repeat pointer-events-none"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='m0 40l40-40h-40v40zm40 0v-40h-40l40 40z'/%3E%3C/g%3E%3C/svg%3E")`
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                    />

                    {/* ✅ FIXED: Content div ko alag se wrap karo */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="flex items-center justify-center px-4 md:px-8 lg:px-12 h-full">
                            <motion.div
                                variants={contentVariants}
                                initial="hidden"
                                animate="visible"
                                className="w-full max-w-auto text-center mx-auto"
                            >
                                {/* Title with character animation */}
                                <motion.h2
                                    className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4 leading-tight"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.3 }}
                                >
                                    {currentSlideData.title.split('').map((char, index) => (
                                        <motion.span
                                            key={index}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.2, delay: 0.4 + index * 0.02 }}
                                            className="inline-block"
                                        >
                                            {char === ' ' ? '\u00A0' : char}
                                        </motion.span>
                                    ))}
                                </motion.h2>

                                {/* Subtitle */}
                                <motion.p
                                    className="text-base md:text-lg lg:text-xl text-gray-200 mb-4 md:mb-6 leading-relaxed font-medium max-w-3xl mx-auto px-4"
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                >
                                    {currentSlideData.subTitle}
                                </motion.p>
                            </motion.div>
                        </div>
                    </div>

                    {/* ✅ ADDED: URL Indicator - Isko pointer-events-none nahi dena */}
                    {/* {currentSlideData?.url && (
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg animate-pulse flex items-center gap-2 backdrop-blur-sm">
                    <FaExternalLinkAlt className="text-xs" />
                    <span>Click Image to Open Link</span>
                </div>
            </div>
        )} */}
                </motion.div>
            </AnimatePresence>
            {/* Navigation Arrows */}
            {carouselSlides.length > 1 && (
                <>
                    <motion.button
                        onClick={prevSlide}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-4 rounded-full transition-all duration-300 z-10 backdrop-blur-sm"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <FaChevronLeft className="text-xl" />
                    </motion.button>

                    <motion.button
                        onClick={nextSlide}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-4 rounded-full transition-all duration-300 z-10 backdrop-blur-sm"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <FaChevronRight className="text-xl" />
                    </motion.button>
                </>
            )}

            {/* Enhanced Dots Indicator */}
            {carouselSlides.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-10">
                    {carouselSlides.map((slide, index) => (
                        <motion.button
                            key={slide._id || slide.id}
                            onClick={() => goToSlide(index)}
                            className={`relative rounded-full transition-all duration-300 ${index === currentSlide
                                ? 'bg-yellow-400'
                                : 'bg-white/50 hover:bg-white/70'
                                }`}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.8 }}
                        >
                            <motion.div
                                className={`rounded-full ${index === currentSlide ? 'w-8 h-2' : 'w-2 h-2'
                                    }`}
                                layout
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                            />
                            {index === currentSlide && (
                                <motion.div
                                    className="absolute inset-0 rounded-full bg-yellow-400"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                />
                            )}
                        </motion.button>
                    ))}
                </div>
            )}

            {/* Enhanced Progress Bar */}
            {carouselSlides.length > 1 && (
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-600/30 backdrop-blur-sm">
                    <motion.div
                        className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 5, ease: "linear" }}
                        key={currentSlide}
                    />
                </div>
            )}

            {/* Slide Counter */}
            {carouselSlides.length > 1 && (
                <motion.div
                    className="absolute top-6 right-6 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm z-10"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                >
                    {currentSlide + 1} / {carouselSlides.length}
                </motion.div>
            )}
        </div>
    );
}

const MASAAwards = () => {
    const [togetherWithMasaData, setTogetherWithMasaData] = useState([]);
    const [participationCategories, setParticipationCategories] = useState([]);
    const [iconImageUrls, setIconImageUrls] = useState({});
    const [loading, setLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categoriesError, setCategoriesError] = useState(null);

    // Helper function to extract media ID from icon field
    const extractMediaId = (icon) => {
        if (!icon) return null;

        // If icon is a string, return it directly
        if (typeof icon === 'string') {
            return icon;
        }

        // If icon is an object, try to get the ID from different possible properties
        if (typeof icon === 'object') {
            return icon.id || icon._id || icon.mediaId || null;
        }

        return null;
    };

    // Get image URL from media ID
    const getImageUrl = async (mediaId) => {
        if (!mediaId) {
            return "https://via.placeholder.com/80/374151/9CA3AF?text=Icon";
        }

        try {
            const response = await axios.get(`${API_MEDIA}/${mediaId}/download`, {
                responseType: 'blob'
            });
            return URL.createObjectURL(response.data);
        } catch (error) {
            console.error(`❌ Error downloading media ${mediaId}:`, error);
            return "https://via.placeholder.com/80/374151/9CA3AF?text=Icon";
        }
    };

    // Fetch Together With Masa data from API
    const fetchTogetherWithMasaData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(API_TOGETHER_WITH_MASA);
            // console.log("Together With Masa API Response:", response.data);

            // Handle different response structures
            const data = response.data.docs || response.data.results || response.data;

            if (Array.isArray(data)) {
                // Filter only active items
                const activeItems = data.filter(item => item.isActive !== false);

                // Fetch icon images for each item
                const itemsWithIcons = await Promise.all(
                    activeItems.map(async (item) => {
                        const mediaId = extractMediaId(item.icon);
                        let iconUrl = "https://via.placeholder.com/80/374151/9CA3AF?text=Icon";

                        if (mediaId) {
                            try {
                                iconUrl = await getImageUrl(mediaId);
                            } catch (error) {
                                console.error(`Error fetching icon for item ${item._id}:`, error);
                            }
                        }

                        return {
                            id: item._id || item.id,
                            title: item.title,
                            description: item.description,
                            iconUrl: iconUrl,
                            isActive: item.isActive,
                            rawIcon: item.icon // Keep for debugging
                        };
                    })
                );

                // console.log("Processed benefits data with icons:", itemsWithIcons);
                setTogetherWithMasaData(itemsWithIcons);
            } else {
                console.error("Unexpected API response format:", data);
                setTogetherWithMasaData([]);
            }
        } catch (error) {
            console.error("❌ Error fetching Together With Masa data:", error);
            setError("Failed to load benefits data");
            setTogetherWithMasaData([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch participation categories from API
    const fetchParticipationCategories = async () => {
        try {
            setCategoriesLoading(true);
            setCategoriesError(null);

            const response = await axios.get(API_PARTICIPATES);
            // console.log("Participation Categories API Response:", response.data);

            // Handle different response structures
            const data = response.data.docs || response.data.results || response.data;

            if (Array.isArray(data)) {
                // Filter only active items
                const activeCategories = data.filter(item => item.isActive !== false);

                // Fetch icon images for each category
                const categoriesWithIcons = await Promise.all(
                    activeCategories.map(async (category) => {
                        const mediaId = extractMediaId(category.icon);
                        let iconUrl = null;

                        if (mediaId) {
                            try {
                                iconUrl = await getImageUrl(mediaId);
                            } catch (error) {
                                console.error(`Error fetching icon for category ${category._id}:`, error);
                            }
                        }

                        return {
                            id: category._id || category.id,
                            name: category.title,
                            iconName: category.title, // Use title for icon mapping
                            iconUrl: iconUrl,
                            isActive: category.isActive,
                            rawIcon: category.icon, // Keep for debugging
                            mediaId: mediaId // Keep for reference
                        };
                    })
                );

                // console.log("Processed participation categories:", categoriesWithIcons);

                // Group categories into chunks of 4 for grid layout
                const groupedCategories = [];
                for (let i = 0; i < categoriesWithIcons.length; i += 4) {
                    groupedCategories.push(categoriesWithIcons.slice(i, i + 4));
                }

                setParticipationCategories(groupedCategories);
            } else {
                console.error("Unexpected API response format for categories:", data);
                setParticipationCategories([]);
            }
        } catch (error) {
            console.error("❌ Error fetching participation categories:", error);
            setCategoriesError("Failed to load participation categories");
            setParticipationCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Get appropriate icon component
    const getIconComponent = (categoryName) => {
        // Try to find matching icon by name
        const matchedKey = Object.keys(iconMap).find(key =>
            categoryName.toLowerCase().includes(key.toLowerCase())
        );
        return iconMap[matchedKey] || iconMap.default;
    };

    useEffect(() => {
        fetchTogetherWithMasaData();
        fetchParticipationCategories();
    }, []);

    // Render loading state
    if (loading || categoriesLoading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="text-center">
                    <FaAward className="text-yellow-400 text-4xl mx-auto mb-4 animate-pulse" />
                    <p className="text-white text-lg">Loading MASA Awards...</p>
                </div>
            </div>
        );
    }

    // Render error state
    if (error || categoriesError) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="text-center">
                    <FaAward className="text-red-400 text-4xl mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">Error Loading Data</h3>
                    <p className="text-gray-300">{error || categoriesError}</p>
                    <button
                        onClick={() => {
                            if (error) fetchTogetherWithMasaData();
                            if (categoriesError) fetchParticipationCategories();
                        }}
                        className="mt-4 bg-yellow-500 text-gray-800 px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Hero Carousel Section */}
            <section className="relative">
                <AwardsCarousel />
            </section>

            {/* Together with MASA Section */}
            <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            TOGETHER WITH <span className="text-yellow-400">MASA</span>
                        </h2>
                        <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Unlock unparalleled opportunities and elevate your business to new heights with MASA Awards
                        </p>
                    </motion.div>

                    {/* Benefits Grid - Dynamic from API with Image Icons */}
                    {togetherWithMasaData.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {togetherWithMasaData.map((benefit, index) => (
                                <motion.div
                                    key={benefit.id}
                                    className="group bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8 hover:bg-gray-800/70 transition-all duration-300 border border-gray-700/50 hover:border-yellow-400/30 hover:transform hover:scale-105"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <div className="flex flex-col items-center text-center">
                                        {/* Icon Image */}
                                        <div className="mb-6 p-4 bg-gray-900/50 rounded-2xl group-hover:bg-yellow-400/10 transition-colors duration-300">
                                            <div className="w-20 h-20 flex items-center justify-center">
                                                <img
                                                    src={benefit.iconUrl}
                                                    alt={benefit.title}
                                                    className="w-16 h-16 object-contain transition-transform duration-300 group-hover:scale-110"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div
                                                    className="hidden w-16 h-16 items-center justify-center text-yellow-400"
                                                    style={{ display: 'none' }}
                                                >
                                                    <FaImage className="text-3xl" />
                                                </div>
                                            </div>
                                        </div>
                                        <h3 className="text-2xl font-bold text-yellow-400 mb-4 group-hover:text-yellow-300 transition-colors duration-300">
                                            {benefit.title}
                                        </h3>
                                        <p className="text-gray-300 group-hover:text-gray-200 transition-colors duration-300 leading-relaxed">
                                            {benefit.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FaAward className="text-gray-400 text-4xl mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-400 mb-2">No Benefits Available</h3>
                            <p className="text-gray-500">Check back later for benefits information.</p>
                        </div>
                    )}
                </div>
            </section>

            <AwardsGallery />

            {/* About MASA Section - Updated Layout */}
            <section className="py-20 bg-black text-white">
                <div className="max-w-7xl mx-auto px-4">
                    {/* First Row - Centered Heading */}
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-yellow-400 leading-tight mb-8">
                            A PLATFORM FOR RECOGNITION OF MSME ENTREPRENEURS, SUCCESSFUL
                            PROFESSIONALS, SOCIALPRENEURS, AND MEDIA PERSONALITIES IMPACTING
                            THE SOCIAL MILLEU WITH CONTRIBUTIONS WORTH CELEBRATING !!
                        </h2>
                    </motion.div>

                    {/* Second Row - Two Columns */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left Column - Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <p className="text-lg text-gray-300 leading-relaxed">
                                <strong className="text-yellow-400">MASA AWARDS</strong>, we're on a mission to
                                make a difference. We were founded on a passion for creating positive change
                                in entrepreneurship and thought leadership where both startups and established
                                brands can share the same platform. Our team's unwavering commitment to innovation,
                                sustainability, and inclusivity drives us forward.
                            </p>

                            <p className="text-lg text-gray-300 leading-relaxed">
                                MASA Awards envisions passionately celebrating and inspiring excellence,
                                propelling businesses to unparalleled success. We foster innovation, and
                                healthy competition, and catalyze positive change in India's dynamic business landscape.
                            </p>

                            <p className="text-lg text-gray-300 leading-relaxed">
                                We offer a collaborative environment that empowers individuals, businesses,
                                and communities to join us in our journey to impact the entrepreneurial,
                                environmental, and social milieu where we can achieve greatness together.
                            </p>

                            {/* Quote Section */}
                            <motion.div
                                className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8 border-l-4 border-yellow-400 mt-8"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                <p className="text-2xl font-bold text-yellow-400 text-center italic">
                                    "Recognition for all who Deserves, not for Reserves"
                                </p>
                            </motion.div>

                            {/* Read More Button */}
                            <motion.button
                                className="px-8 py-4 mt-6 bg-yellow-500 text-black font-bold text-lg rounded-lg hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-yellow-500/25"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                READ MORE ABOUT US
                            </motion.button>
                        </motion.div>

                        {/* Right Column - Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="flex justify-center"
                        >
                            <div className="relative w-full max-w-md">
                                <motion.img
                                    src="https://www.masaawards.com/admin/media/post/1704981348b.png"
                                    alt="MASA Awards"
                                    className="w-full h-auto rounded-2xl shadow-2xl shadow-yellow-500/20 border-4 border-yellow-400"
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ duration: 0.3 }}
                                />
                                {/* Optional: Add decorative elements */}
                                <motion.div
                                    className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full"
                                    initial={{ scale: 0, rotate: 0 }}
                                    whileInView={{ scale: 1, rotate: 360 }}
                                    transition={{ duration: 0.6, delay: 0.5 }}
                                    viewport={{ once: true }}
                                />
                                <motion.div
                                    className="absolute -bottom-4 -left-4 w-6 h-6 bg-yellow-400 rounded-full"
                                    initial={{ scale: 0, rotate: 0 }}
                                    whileInView={{ scale: 1, rotate: -360 }}
                                    transition={{ duration: 0.6, delay: 0.7 }}
                                    viewport={{ once: true }}
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Who Can Participate Section */}
            <section className="py-16 bg-gradient-to-br from-gray-800 to-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            WHO CAN <span className="text-yellow-400">PARTICIPATE</span>
                        </h2>
                        <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
                        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                            MASA Awards is getting hosted to recognise the various Companies, Entrepreneurs, Service Providers and Individuals for their outstanding performance and achievements in their respective field.
                        </p>
                    </motion.div>

                    {/* Categories Grid - Dynamic from API */}
                    {participationCategories.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {participationCategories.map((categoryGroup, groupIndex) => (
                                <motion.div
                                    key={groupIndex}
                                    className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8 border border-gray-700/50 hover:border-yellow-400/30 transition-all duration-300"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: groupIndex * 0.1 }}
                                    viewport={{ once: true }}
                                    whileHover={{ scale: 1.02 }}
                                >
                                    <div className="space-y-4">
                                        {categoryGroup.map((category, index) => {
                                            const IconComponent = getIconComponent(category.name);
                                            return (
                                                <motion.div
                                                    key={category.id}
                                                    className="flex items-center gap-4 p-3 bg-gray-700/30 rounded-xl hover:bg-gray-700/50 transition-all duration-300 group"
                                                    whileHover={{ scale: 1.05 }}
                                                >
                                                    {/* Display uploaded icon if available, otherwise use default icon */}
                                                    {category.iconUrl ? (
                                                        <div className="flex-shrink-0 w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center group-hover:bg-yellow-300 transition-colors duration-300 overflow-hidden">
                                                            <img
                                                                src={category.iconUrl}
                                                                alt={category.name}
                                                                className="w-6 h-6 object-contain"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                            <div className="hidden w-6 h-6 items-center justify-center">
                                                                <IconComponent className="text-gray-800 text-sm" />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex-shrink-0 w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center group-hover:bg-yellow-300 transition-colors duration-300">
                                                            <IconComponent className="text-gray-800 text-lg" />
                                                        </div>
                                                    )}
                                                    <span className="text-lg font-semibold text-white group-hover:text-yellow-300 transition-colors duration-300">
                                                        {category.name}
                                                    </span>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FaAward className="text-gray-400 text-4xl mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-400 mb-2">No Participation Categories Available</h3>
                            <p className="text-gray-500">Check back later for participation information.</p>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default MASAAwards;